import { useRef, useState } from "react";
import { Box, Typography, styled } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import {
  generateID,
  getReadableTimestamp,
} from "../../../utils/informationUtility";
import { useDispatch, useSelector } from "react-redux";
import {
  createBot,
  deleteBotFromTemplateBots,
  deleteChatbot,
  generateQuestions,
  getBotbyOrgTemplates,
  getCombinations,
  initializeCombinationsSync,
  saveOptimizedBot,
} from "../../../features/chatbots/chatbotSlice";
import {
  deleteBotFromOrg,
  getOrganization,
} from "../../../features/organization/orgSlice";
import {
  AGGRESSION_PROMPT,
  TEXT_TO_NO_SQL_PROMPT,
} from "../../../utils/defaultPrompts";
import axios from "axios";
import { sendBugReport } from "../../../utils/sendBugReport";
import QuestionnaireCarousel from "./Questionnaire";
import { createNewMemorySession } from "../../../features/auth/authSlice";

// Styled components
const UploadBox = styled(Box)(({ theme, isDragging }) => ({
  width: "50%",
  height: 350,
  border: `2px dashed var(--buttonBackground)`,
  borderRadius: theme.shape.borderRadius,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  position: "relative",
  transition: "background-color 0.3s ease",
  backgroundColor: isDragging ? "rgba(25, 118, 210, 0.05)" : "transparent",
  "&:hover": {
    backgroundColor: "rgba(25, 118, 210, 0.05)",
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.grey[300],
  borderRadius: "50%",
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const HiddenInput = styled("input")({
  display: "none",
});

const ACCEPTED_FORMATS = [".doc", ".docx", ".txt", ".pdf"];

const FileUploadArea = ({
  datasets,
  setDatasets,
  isDragging,
  setIsDragging,
  setLoading,
  loading,
  showSnackbar,
  botData,
  setBotData,
  setRetrievalCombinationId,
  setRetrievalCombinationId2,
  setGenerationCombinationId,
  setShowQuestionnaire,
  showQuestionnaire,
  setShowOptimizationMessage,
  setCurrentQuestion,
  currentQuestion,
  setQuestionnaireMessages,
  setQuestionnaireStarted,
  questionnaireStarted,
  setQuestionnaireCompleted,
  questionnaireCompleted,
  setOptimizedRag,
  render_method,
  setShowFileUploadArea,
  setRetrievalCombinations,
  setBot,
  bot,
  setLoadingMessage,
  loadingMessage,
  setGenerationCombinationId2,
  retrievalCombinations,
  optimizedRag,
  questionnaireMessages,
}) => {
  const { organization } = useSelector((state) => state.organization);
  const { user, botMemory } = useSelector((state) => state.auth);

  const fileInputRef = useRef(null);

  const [priorityList, setPriorityList] = useState([
    "Accuracy",
    "Cost",
    "Latency",
  ]); // Draggable priority list
  const dispatch = useDispatch();
  const userId = user?.user?._id?.["$oid"];
  const orgId = organization?._id?.["$oid"];

  const [domain, setDomain] = useState("");

  const questions = [
    {
      question:
        "Are keyword searches helpful in finding the correct information in your uploaded documents?",
      options: ["Yes", "No"],
    },
    {
      question: "Do your documents contain tables, images, or charts?",
      options: ["Yes", "No"],
    },
    // {
    //   question:
    //     "Are your documents related to a specific domain or subject area?",
    //   options: ["Yes", "No"],
    // },
    {
      question: "Drag and arrange the metrics below to change priorities.",
      options: [],
    },
  ];

  // Function to handle file validation
  const validateFiles = (files) => {
    const validFiles = [];
    const invalidFiles = [];

    files.forEach((file) => {
      const fileExtension = file.name.substring(file.name.lastIndexOf("."));
      if (ACCEPTED_FORMATS.includes(fileExtension)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      showSnackbar(
        `Invalid files: ${invalidFiles.join(
          ", "
        )}. Accepted file types: ${ACCEPTED_FORMATS.join(", ")}.`,
        "error"
      );
    }

    return validFiles;
  };

  // Function to process valid files with FileReader
  const processFiles = async (files) => {
    const newDatasets = [];
    setShowFileUploadArea(false);
    if (
      organization?.templates?.length > 0 &&
      organization?.subscription === "starter"
    ) {
      alert(
        "You can create only one application for starter package. Please upgrade to Queryloop Pro to create more applications"
      );
      setLoading(false);
      setLoadingMessage("");

      return;
    }
    if (
      organization?.subscription === "starter" &&
      (botData?.datasets?.length > 5 || files.length > 5)
    ) {
      alert(
        "You can upload only 5 files with the Starter package. Please upgrade to Queryloop Pro to upload more files."
      );
      setLoading(false);
      setLoadingMessage("");

      return;
    }
    const processFile = (file, index) =>
      new Promise((resolve) => {
        const reader = new FileReader();
        const uniqueId = generateID(8) + "_" + index;
        const fileName = file.name;
        const fileSize = file.size;

        reader.onload = (event) => {
          const newDataset = {
            filedata: event.target.result,
            file: file,
            id: uniqueId,
            fileName: fileName,
            fileType: file.type,
            creationDate: generateID(8),
            category: "",
            confidentiality: "Public",
            fileSize: fileSize,
            scrapper: false,
          };
          resolve(newDataset);
        };

        reader.readAsDataURL(file);
      });

    for (let i = 0; i < files.length; i++) {
      const processedFile = await processFile(files[i], i);
      newDatasets.push(processedFile);
    }

    // Update bot data and call createBot
    const updatedBotData = {
      ...botData,
      organization: orgId,
      creator: userId,
      name: `${newDatasets[0].fileName}_${getReadableTimestamp()}`,
      get_dataset_from_bot: "",
      datasets: [...botData.datasets, ...newDatasets],
      templates: true,
      golden_responses_option: "generateQna",
      num_of_qa_pairs: 5,
      text2nosql: false,
      "text2nosql-vectorsearch": true,
      text2nosql_prompt: {
        TEXT_TO_NO_SQL_PROMPT: TEXT_TO_NO_SQL_PROMPT,
      },
      aggregation_prompt: AGGRESSION_PROMPT,
    };

    if (Object.keys(updatedBotData).length === 0) {
      alert(
        "An error occurred while uploading datasets. Please refresh the application and try again."
      );
      setLoading(false);
      setLoadingMessage("");

      return;
    }
    setBotData(updatedBotData);

    // Call createBot with updated bot data
    if (organization?.subscription === "starter") {
      handleCreateBot(updatedBotData);
    } else {
      setOptimizedRag({
        top_k: 10,
        chunksize: "1800",
        retrieval_method: "sentence-window",
      });
      setLoading(false);
      setShowQuestionnaire(true);
      setShowOptimizationMessage(false); // Reset questionnaire state if reopened
      setCurrentQuestion(0); // Reset question index
      setQuestionnaireMessages([]); // Clear previous messages
      setQuestionnaireStarted(true);
      setQuestionnaireCompleted(false);
    }
  };

  // Function to handle file input and drop events
  const handleFiles = async (files) => {
    setBot({});
    setLoading(true);
    setLoadingMessage("Processing datasets.");
    const validFiles = validateFiles(files);
    if (validFiles.length > 0) {
      await processFiles(validFiles);
    } else {
      setLoading(false);
      setLoadingMessage("");

      alert(
        "Invalid files. Please upload files with the following extensions: .doc, .docx, .txt, .pdf"
      );
      return;
    }
  };

  const applyDecisionLogic = (priorities) => {
    const accuracyIndex = priorities.indexOf("Accuracy") + 1;
    const costIndex = priorities.indexOf("Cost") + 1;
    const latencyIndex = priorities.indexOf("Latency") + 1;
    setBotData((prevData) => {
      const updatedData = {
        ...prevData,
        text2nosql: false,
        "text2nosql-vectorsearch": true,
        text2nosql_prompt: {
          TEXT_TO_NO_SQL_PROMPT: TEXT_TO_NO_SQL_PROMPT,
        },
        aggregation_prompt: AGGRESSION_PROMPT,
      };
      const ragData = { ...optimizedRag };
      // Decision Logic
      if (accuracyIndex === 1) {
        if (costIndex === 2 && latencyIndex === 3) {
          updatedData.workflow.retrieval_1.query_preprocess_method = {
            ...updatedData.workflow.retrieval_1.query_preprocess_method,
            "multi-query": true,
          };
          ragData["query_preprocess_method"] = "multi-query";
          if (
            updatedData.workflow.retrieval_1.parser[
              "recursive_character_text_splitter"
            ]
          ) {
            updatedData.workflow.retrieval_1.rerank_method = {
              ...updatedData.workflow.retrieval_1.rerank_method,
              none: true,
              llm_rerank: true,
            };
            ragData["rerank_method"] = "llm_rerank";
          }
        } else if (latencyIndex === 2 && costIndex === 3) {
          updatedData.workflow.generation_1.llm_params.llm = {
            ...updatedData.workflow.generation_1.llm_params.llm,
            "claude-3-7-sonnet-20250219":
              organization.subscription !== "starter",
            "gpt-4o-mini": true,
          };
          if (
            updatedData.workflow.retrieval_1.parser[
              "recursive_character_text_splitter"
            ]
          ) {
            updatedData.workflow.retrieval_1.rerank_method = {
              ...updatedData.workflow.retrieval_1.rerank_method,
              none: true,
              cohere: organization.subscription !== "starter",
            };
            if (organization.subscription !== "starter") {
              ragData["rerank_method"] = "cohere";
            } else {
              ragData["rerank_method"] = "none";
            }
          }
        }
        //if not llama parse then select cohere if latency high, if latency low then llm_rerank
      } else if (accuracyIndex === 2 && latencyIndex === 1) {
        if (
          updatedData.workflow.retrieval_1.parser[
            "recursive_character_text_splitter"
          ]
        ) {
          updatedData.workflow.retrieval_1.rerank_method = {
            ...updatedData.workflow.retrieval_1.rerank_method,
            none: true,
            cohere: organization.subscription !== "starter",
          };
          ragData["rerank_method"] =
            organization.subscription !== "starter" ? "none" : "cohere";
        }
      }
      setOptimizedRag((prev) => ({ ...prev, ...ragData }));
      return updatedData;
    });
  };

  const generatePrompt = async (problem_statement) => {
    const formData = new FormData();
    let _data = {
      problem_statement: problem_statement || "I want a helpful assistant.",
      prompt_type: "zero-shot",
      prompt_text: "\nReturn sources of the documents used in context.",
      retreival: true,
    };
    for (const [key, value] of Object.entries(_data)) {
      if (key === "prompt_text") {
        formData.append(key, JSON.stringify(value));
        continue;
      }
      formData.append(key, value);
    }

    const apiEndpoint = `/api/eval_bot/generate/prompt/zero-shot/${userId}`;
    const token = JSON.parse(localStorage.getItem("token"));
    let PROMPT = "";
    // Making the POST request
    await axios
      .post(apiEndpoint, formData, {
        headers: {
          // Include the bearer token in the authorization header
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        // Handle success
        PROMPT = response.data.prompt;
        return PROMPT;
      })
      .catch((error) => {
        // Handle error
        console.error("Error:", error);
        sendBugReport(
          organization?.organization,
          user?.user,
          error,
          "Prompt Generation",
          dispatch
        );
      });

    return PROMPT;
  };

  const handleCreateBot = async (_botData_) => {
    setLoading(true);
    setLoadingMessage("Creating LLM application.");
    let __TEMPLATES__CHECK__ = true;
    const __PROMPT__ = await generatePrompt(
      botData?.prompt_combinations?.["zero-shot"]
    );
    let prompt_combinations = { ..._botData_.prompt_combinations };
    if (__PROMPT__) {
      prompt_combinations["zero-shot"] = __PROMPT__;
    }

    const orgId = organization?._id?.["$oid"];
    if (!_botData_?.templates) {
      __TEMPLATES__CHECK__ = false;
    }
    const formData = new FormData();
    for (const [key, value] of Object.entries(_botData_)) {
      let d_ = {};
      if (key === "datasets") {
        _botData_.datasets.map((d, index) => (d_[key + index] = d));

        // Handle datasets - maintain metadata structure but add files directly
        botData.datasets.forEach((d, index) => {
          // Create a copy of the dataset object without file and filedata
          const datasetMetadata = { ...d };

          // Extract the file object before removing it from metadata
          const fileObject = d.file instanceof File ? d.file : null;

          // Remove file-related fields from metadata
          delete datasetMetadata.file;
          delete datasetMetadata.filedata;

          // Store metadata in d_
          d_[key + index] = datasetMetadata;

          // If it's a File object, add it directly to FormData
          if (fileObject) {
            formData.append(`${key}${index}_file`, fileObject);
          }
        });

        // Add metadata structure as JSON
        formData.append(key, JSON.stringify(d_));
        continue;
      } else if (key === "finetuning_dataset") {
        formData.append(key, JSON.stringify({}));
        continue;
      } else if (key === "dynamicShot_dataset") {
        formData.append(key, JSON.stringify({}));
        continue;
      } else if (key === "parameter_key_names") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "llm_params") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "prompt_combinations") {
        formData.append(key, JSON.stringify(prompt_combinations));
        continue;
      } else if (key === "finetuning_values") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "golden_responses") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "tone") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "response_length") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "references_text") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "columnTypes") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "unstructuredColumnTypes") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "structured_datasets") {
        _botData_.structured_datasets.map((d, index) => (d_[key + index] = d));
        formData.append(key, JSON.stringify(d_));

        continue;

        // botData.structured_datasets.map((d, index) => (d_[key + index] = d));
        // formData.append(key, JSON.stringify(d_));
        // continue;
      } else if (key === "creator") {
        formData.append(key, user?.user?._id["$oid"]);
        continue;
      } else if (key === "workflow") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "headers") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "metadataDescription") {
        formData.append(key, JSON.stringify({}));
        continue;
      } else if (key === "query_preprocessing_prompts") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "neo4j_credentials") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "neo_4j_prompts") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "organization") {
        formData.append(key, orgId);
        continue;
      } else if (key === "retrieval_prompts") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "parsing_instructions") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "text2nosql") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "text2nosql_prompt") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "text2nosql-vectorsearch") {
        formData.append(key, JSON.stringify(value));
        continue;
      } else if (key === "aggregation_prompt") {
        formData.append(key, JSON.stringify(value));
        continue;
      }
      formData.append(key, value);
    }
    let prompts = [
      _botData_.response_length_text || "",
      _botData_.recency_text || "",
      _botData_.tone_text || "",
      _botData_.references_text || "",
      _botData_.sources_text || "",
    ];
    let prompt_guides = prompts.filter((prompt) => prompt !== "");

    formData.append("prompt_guides", JSON.stringify(prompt_guides));
    if (!__TEMPLATES__CHECK__) {
      formData.append("templates", true);
    }

    const responseCreate = await dispatch(createBot(formData));
    if (responseCreate?.payload?.status !== "Successfuly created") {
      alert(
        "An error occurred while creating the application. Please contact support."
      );
      setLoading(false);
      setLoadingMessage("");
      setShowQuestionnaire(false);
      setQuestionnaireCompleted(false);
      setQuestionnaireStarted(false);
      setShowFileUploadArea(true);
      return;
    }
    setLoadingMessage("Generating benchmark responses.");
    const botID = responseCreate?.payload?.combinations?.[0]?._id?.["$oid"];
    const goldenQnAGenerationResponse = await dispatch(
      generateQuestions({ bot_id: botID, num_of_qa_pairs: "5" })
    );
    const golden_qa_list = Object.entries(
      goldenQnAGenerationResponse?.payload?.golden_qa_dict
    ).map(([question, answer]) => ({
      question,
      answer,
    }));
    const retrievalID =
      responseCreate?.payload?.combinations?.[0]?.combinations?.[0]?.["$oid"];
    const generationId =
      responseCreate?.payload?.combinations?.[0]
        ?.generation_combinations?.[0]?.["$oid"];
    let retrievalID2 =
      responseCreate?.payload?.combinations?.[0]?.combinations?.[1]?.["$oid"];
    const generationId2 =
      responseCreate?.payload?.combinations?.[0]
        ?.generation_combinations?.[1]?.["$oid"];

    setLoadingMessage(
      "Creating embeddings, Preparing indexes, Uploading data in vector database."
    );

    const responseRetrievalCombinationGet0 = await dispatch(
      getCombinations({ id: botID, items: 100, from: 0, data: {} })
    );

    const __OPTIMIZED__BOT__COMBINATION__ = getMatchingCombinationId(
      optimizedRag,
      responseRetrievalCombinationGet0.payload.combinations
    );

    if (!__OPTIMIZED__BOT__COMBINATION__) {
      alert(
        "An error occurred while creating the application. Please contact support."
      );
      await handleDelete(botID);
      setLoading(false);
      setLoadingMessage("");
      setShowQuestionnaire(false);
      setQuestionnaireCompleted(false);
      setQuestionnaireStarted(false);
      setShowFileUploadArea(true);

      return;
    }
    retrievalID2 = __OPTIMIZED__BOT__COMBINATION__;
    await dispatch(
      saveOptimizedBot({
        combination_id: __OPTIMIZED__BOT__COMBINATION__,
        org_id: orgId,
        botID: botID,
      })
    );
    const responseInit = await dispatch(
      initializeCombinationsSync({
        user_id: userId,
        bot_id: botID,
        orgId: orgId,
      })
    );
    if (
      responseInit?.payload?.status !== "successfully initialized environment!"
    ) {
      alert(
        "An error occurred while creating the application. Please contact support."
      );
      await handleDelete(botID);
      setLoading(false);
      setLoadingMessage("");
      setShowQuestionnaire(false);
      setQuestionnaireCompleted(false);
      setQuestionnaireStarted(false);
      setShowFileUploadArea(true);

      return;
    }
    setLoadingMessage("Optimizing your application.");

    const res = await dispatch(
      createNewMemorySession({
        user_id: userId,
        bot_id: botID,
        sessionTitle: "unOptimized",
      })
    );
    const resSlightlyOptimized = await dispatch(
      createNewMemorySession({
        user_id: userId,
        bot_id: botID,
        sessionTitle: "slightlyOptimized",
      })
    );

    setBot({
      ...responseCreate?.payload?.combinations?.[0],
      initialised: "initialised",
      golden_responses: golden_qa_list,
    });

    const responseRetrievalCombinationGet = await dispatch(
      getCombinations({ id: botID, items: 100, from: 0, data: {} })
    );

    setRetrievalCombinations(
      responseRetrievalCombinationGet?.payload?.combinations || []
    );

    setRetrievalCombinationId(retrievalID);
    setGenerationCombinationId(generationId);
    setRetrievalCombinationId2(__OPTIMIZED__BOT__COMBINATION__ || retrievalID2);
    setGenerationCombinationId2(generationId2);
    const responseGetOrg = await dispatch(getOrganization(orgId));
    const responseGetBotOrgTemplates = await dispatch(
      getBotbyOrgTemplates(orgId)
    );

    setLoadingMessage("Almost done.");
    setTimeout(() => {
      setShowQuestionnaire(false);
      setLoading(false);
      setLoadingMessage("");
      setShowFileUploadArea(false);
    }, 5000);
  };

  function getMatchingCombinationId(optimizedRag, combinations) {
    const optimizedKeys = Object.keys(optimizedRag);
    for (const combination of combinations) {
      const isMatch = optimizedKeys.every((key) => {
        if (optimizedRag[key]) {
          return combination[key] === optimizedRag[key];
        }
        return combination[key] === optimizedRag[key];
      });
      if (isMatch) {
        return combination._id["$oid"];
      }
    }

    // If no match is found, return null
    return null; //combinations[combinations.length - 1]._id["$oid"];
  }

  // Event handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    await handleFiles(files);
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    await handleFiles(files);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleDelete = async (id) => {
    setLoadingMessage("An error has occurred while creating the application.");
    const orgID = organization?.organization?._id?.["$oid"];
    const response = await dispatch(deleteChatbot(id));
    if (orgID) {
      const responseDelete = await dispatch(getBotbyOrgTemplates(orgID));
      await dispatch(deleteBotFromOrg(id));
      await dispatch(deleteBotFromTemplateBots(id));
    }

    setShowFileUploadArea(true);
    setDatasets([]);
  };

  return (
    <>
      {!showQuestionnaire && (
        <UploadBox
          isDragging={isDragging}
          onClick={handleClick}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <IconContainer>
            <CloudUploadIcon color="primary" sx={{ fontSize: 32 }} />
          </IconContainer>
          <Typography color="primary">
            Upload/Drag and drop your documents
          </Typography>
          <HiddenInput
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            accept={ACCEPTED_FORMATS.join(",")}
          />
        </UploadBox>
      )}

      {/* Questionnaire Window */}
      {showQuestionnaire && (
        <QuestionnaireCarousel
          priorityList={priorityList}
          setPriorityList={setPriorityList}
          questions={questions}
          botData={botData}
          setBotData={setBotData}
          applyDecisionLogic={applyDecisionLogic}
          handleCreateBot={handleCreateBot}
          organization={organization}
          domain={domain}
          setDomain={setDomain}
        />
      )}
    </>
    // )
    //  : (
    //   <>
    //     {/* Button styled file upload */}
    //     <Button
    //       variant="contained"
    //       onClick={handleClick}
    //       sx={{
    //         display: "flex",
    //         alignItems: "center",
    //         justifyContent: "center",
    //         gap: 1,
    //         padding: "5px 10px",
    //         backgroundColor: "#3498db",
    //         color: "#fff",
    //         fontWeight: "bold",
    //         fontSize: "12px",
    //         textTransform: "none",
    //         "&:hover": {
    //           backgroundColor: "#2980b9",
    //         },
    //       }}
    //     >
    //       <CloudUploadIcon sx={{ fontSize: 20, marginRight: "8px" }} />+ Create
    //       new application
    //     </Button>
    //     <HiddenInput
    //       ref={fileInputRef}
    //       type="file"
    //       multiple
    //       onChange={handleFileSelect}
    //       accept={ACCEPTED_FORMATS.join(",")}
    //     />
    //   </>
  );
};

export default FileUploadArea;
